import { useState, useMemo } from 'react';
import { gql, useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FormControl, Autocomplete, TextField, Box, Button, Grid, CircularProgress } from '@mui/material';
import { IUser } from '../../../types/user';
import { FlexEnd } from '../../../commonStyles';
import StandardTable from '../../tables/StandardTable';
import { convertAllDates } from '../../../utils/Functions';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../features/auth/authSlice';
import { IProject } from '../../../types/project';
import { useAppDispatch } from '../../../app/hooks';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../features/snackbar/snackbarSlice';
import { IContract } from '../../../types/user';

const DealTracker = () => {
  const user = useSelector(selectUser);
  const storeDispatch = useAppDispatch();
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [users, setUsers] = useState<IUser[]>([]);
  const [add, setAdd] = useState<boolean>(false);
  const [project, setProject] = useState<IProject | null>(null);
  const [dateStart, setDateStart] = useState<Date | null>(null);
  const [dateEnd, setDateEnd] = useState<Date | null>(null);
  const [edit, setEdit] = useState<number>();

  const [updateUser] = useMutation(UPDATEUSER, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Contracts has been updated'));
    },
    onError: (err: any) => {
      console.log(err, 'err');
    },
  });

  const [getContractSummary, { loading }] = useLazyQuery(GETCONTRACTS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Contracts Summary has been emailed'));
    },
    onError: (err) => {
      console.log(err);
    },
  });

  useQuery(GETUSERS, {
    variables: {
      filter: { OR: [{ type: 'Sales' }, { type: 'Manager' }] },
    },
    onCompleted: (data) => {
      let sortedUser = [...data.userMany].sort((a, b) => a.fullName.localeCompare(b.fullName));
      setUsers(sortedUser);
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const columns = useMemo(() => {
    return [
      {
        Header: 'Project',
        accessor: (rowData: IContract) => rowData.project.name,
      },
      {
        Header: 'Date Start',
        accessor: (rowData: IContract, index: number) => {
          if (index === edit) {
            return (
              <FormControl required sx={{ width: '100%' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={'Contract Start Date'}
                    value={dateStart}
                    onChange={(newValue) => {
                      handleDate(newValue, 'dateStart', index);
                    }}
                    sx={{ width: '100%' }}
                  />
                </LocalizationProvider>
              </FormControl>
            );
          } else {
            return convertAllDates(rowData.dateStart, 'PP');
          }
        },
      },
      {
        Header: 'Date End',
        accessor: (rowData: IContract, index: number) => {
          if (index === edit) {
            return (
              <FormControl required sx={{ width: '100%' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={'Contract End Date'}
                    value={dateEnd}
                    onChange={(newValue) => {
                      handleDate(newValue, 'dateEnd', index);
                    }}
                    sx={{ width: '100%' }}
                  />
                </LocalizationProvider>
              </FormControl>
            );
          } else {
            return convertAllDates(rowData.dateEnd, 'PP');
          }
        },
      },
      {
        Header: 'Edit',
        accessor: (rowData: IContract, index: number) => {
          if (index === edit) {
            return (
              <Button variant="contained" color="success" onClick={() => setEdit(undefined)}>
                Back
              </Button>
            );
          } else {
            return (
              <Button variant="contained" color="primary" onClick={() => setEdit(index)}>
                Edit
              </Button>
            );
          }
        },
      },
      {
        Header: 'Delete',
        accessor: (rowData: IContract, index: number) => {
          return (
            <Button variant="contained" color="error" onClick={() => handleDelete(index)}>
              Delete
            </Button>
          );
        },
      },
    ];
  }, [edit, selectedUser]);

  const addContract = (e: any) => {
    e.preventDefault();
    if (!project) return storeDispatch(showErrorSnackbar('No Project Selected'));
    if (!dateStart || !dateEnd) return storeDispatch(showErrorSnackbar('Invalid Date Range'));

    setSelectedUser({
      ...selectedUser!,
      contracts: [
        ...selectedUser?.contracts!,
        {
          project,
          dateStart,
          dateEnd,
        },
      ],
    });
  };

  const handleDate = (value: Date | null, type: string, numIndex: number) => {
    if (selectedUser) {
      let contracts = selectedUser?.contracts!.map((contract: IContract, index: number) => {
        if (index === numIndex) {
          return {
            ...contract,
            [type]: value,
          };
        } else return contract;
      });

      setSelectedUser({
        ...selectedUser,
        contracts,
      });
    }
  };

  const handleDelete = (numIndex: number) => {
    setSelectedUser({
      ...user,
      contracts: selectedUser?.contracts.filter((contract: IContract, index: number) => index !== numIndex),
    });
  };

  return (
    <div>
      <FormControl required sx={{ mt: 2, width: '300px' }}>
        <Autocomplete
          id="task-users"
          options={users}
          freeSolo={false}
          getOptionLabel={(option: any) => option.fullName}
          isOptionEqualToValue={(option, value) => option === value}
          value={selectedUser}
          onChange={(e, value) => setSelectedUser(value)}
          renderInput={(params) => <TextField {...params} variant="outlined" label="Users" />}
        />
      </FormControl>
      <Box>
        <FlexEnd>
          <Button disabled={loading} variant="contained" color="success" onClick={() => getContractSummary()}>
            {loading ? <CircularProgress size={16} /> : 'Download All'}
          </Button>
          {selectedUser ? (
            <>
              <Button
                sx={{ ml: 2 }}
                variant="contained"
                color="success"
                disabled={loading}
                onClick={() => getContractSummary({ variables: { userId: selectedUser._id } })}
              >
                {loading ? <CircularProgress size={16} /> : `Download ${selectedUser.fullName}'s Summary`}
              </Button>
              <Button sx={{ ml: 2 }} variant="contained" color="primary" onClick={() => setAdd(!add)}>
                {add ? 'Go Back' : 'Add New Contract'}
              </Button>
            </>
          ) : null}
        </FlexEnd>
      </Box>
      <Box sx={{ mt: 2 }}>
        {add ? (
          <form onSubmit={addContract}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <FormControl required sx={{ width: '100%' }}>
                  <Autocomplete
                    sx={{ height: '100%' }}
                    options={[user?.projectAccess!.map((projectAccess: any) => projectAccess?.project)].flat()}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    disableClearable={false}
                    freeSolo={false}
                    value={project}
                    onChange={(e, value) => {
                      setProject(value!);
                    }}
                    renderInput={(params) => <TextField {...params} label="Project" size="medium" />}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl required sx={{ width: '100%' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label={'Contract Start Date (YYYY/MM/DD)'}
                      value={dateStart}
                      onChange={(newValue) => {
                        setDateStart(newValue);
                      }}
                      sx={{ width: '100%' }}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl required sx={{ width: '100%' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label={'Contract End Date (YYYY/MM/DD)'}
                      value={dateEnd}
                      onChange={(newValue) => {
                        setDateEnd(newValue);
                      }}
                      sx={{ width: '100%' }}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
            </Grid>
            <FlexEnd sx={{ mb: 2 }}>
              <Button type="submit" variant="contained" color="success">
                Add Contract Range
              </Button>
            </FlexEnd>
          </form>
        ) : null}
        <StandardTable columns={columns} data={selectedUser ? selectedUser?.contracts : []} />
        {selectedUser ? (
          <FlexEnd sx={{ mt: 2 }}>
            <Button
              onClick={() => {
                updateUser({
                  variables: {
                    _id: selectedUser?._id,
                    record: {
                      contracts: selectedUser?.contracts.map((contract: IContract) => {
                        return {
                          ...contract,
                          project: contract.project._id,
                        };
                      }),
                    },
                  },
                });
              }}
              variant="contained"
              color="success"
            >
              Save
            </Button>
          </FlexEnd>
        ) : null}
      </Box>
    </div>
  );
};

const GETUSERS = gql`
  query userMany($filter: FilterFindManyUserInput!) {
    userMany(filter: $filter) {
      _id
      email
      fullName
      contracts {
        project {
          name
          _id
        }
        dateStart
        dateEnd
      }
    }
  }
`;

const UPDATEUSER = gql`
  mutation userUpdateById($_id: MongoID!, $record: UpdateByIdUserInput!) {
    userUpdateById(_id: $_id, record: $record) {
      record {
        _id
        email
        fullName
        contracts {
          project {
            name
            _id
          }
          dateStart
          dateEnd
        }
      }
    }
  }
`;

const GETCONTRACTS = gql`
  query getContractSummary($userId: MongoID) {
    getContractSummary(userId: $userId)
  }
`;

export default DealTracker;
